import { motion, useInView } from 'motion/react';
import { useRef, useState, useEffect } from 'react';
import {Award, Coffee, Code2, Users, CalendarIcon} from 'lucide-react';

export function Stats() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  const stats = [
    { icon: Code2, value: 15, suffix: '+', label: 'Projects Completed', color: 'from-cyan-400 to-blue-500' },
    { icon: Users, value: 10, suffix: '+', label: 'Happy Clients', color: 'from-blue-400 to-purple-500' },
    { icon: CalendarIcon, value: 5, suffix: '+', label: 'Years of Experience', color: 'from-purple-400 to-pink-500' },
    { icon: Coffee, value: 999, suffix: '+', label: 'Cups of Coffee', color: 'from-pink-400 to-red-500' },
  ];

  return (
    <section className="py-32 px-6 relative overflow-hidden" ref={ref}>
      {/* Animated background grid */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(rgba(34, 211, 238, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(34, 211, 238, 0.1) 1px, transparent 1px)',
          backgroundSize: '50px 50px',
        }} />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <StatCard
              key={stat.label}
              stat={stat}
              index={index}
              isInView={isInView}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function StatCard({ stat, index, isInView }: { stat: any; index: number; isInView: boolean }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isInView) return;

    let start = 0;
    const end = stat.value;
    const duration = 2000;
    const increment = end / (duration / 16);

    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);

    return () => clearInterval(timer);
  }, [isInView, stat.value]);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.5 }}
      animate={isInView ? { opacity: 1, scale: 1 } : {}}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="relative group"
    >
      <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm border border-cyan-500/20 rounded-2xl p-8 text-center hover:border-cyan-500/50 transition-all hover:shadow-2xl hover:shadow-cyan-500/20 hover:scale-105">
        {/* Glow effect */}
        <div className={`absolute inset-0 bg-gradient-to-r ${stat.color} opacity-0 group-hover:opacity-10 rounded-2xl transition-opacity blur-xl`} />
        
        <div className="relative z-10">
          <div className={`inline-flex p-4 bg-gradient-to-r ${stat.color} rounded-xl mb-4`}>
            <stat.icon className="text-white" size={32} />
          </div>
          <div className={`bg-gradient-to-r ${stat.color} bg-clip-text text-transparent mb-2`}>
            {count}{stat.suffix}
          </div>
          <p className="text-slate-400 text-sm">{stat.label}</p>
        </div>
      </div>
    </motion.div>
  );
}
